import React, { FC, useEffect, useState, useCallback } from 'react'
import { Form, Formik, FormikProps } from 'formik'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import { showErrorToast, showSuccessToast } from '../../../../shared/components/ToastUtils'
import { ConfirmModal } from '../../../../shared/components/ConfirmModal'
import { IRoleModel, IPermission, initialRole, validationRole, MODULE_LABELS } from './RoleModel'
import { createRole, updateRole, deleteRole, loadRoles, loadAllPermissions, loadAllMenus } from './Api'
import TextInput from '../../../../shared/components/TextInput'
import RoleTable from './RoleTable'
import { loadMenuTrees } from '../menu/Api'
import { IMenuTreeModel } from '../menu/MenuModel'

const RolePage: FC = () => {
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [roles, setRoles] = useState<IRoleModel[]>([])
  const [permissions, setPermissions] = useState<IPermission[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [tab, setTab] = useState<'form' | 'list'>('form')
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])
  const [permissionSearch, setPermissionSearch] = useState('')
  const [menuModuleLabels, setMenuModuleLabels] = useState<Record<string, string>>({})
  // Yetkiler seçili menü ağacının (yazılımın) menülerinden üretilir
  const [menuTrees, setMenuTrees] = useState<IMenuTreeModel[]>([])
  const [selectedTreeId, setSelectedTreeId] = useState('')

  useEffect(() => {
    loadMenuTrees()
      .then((response) => {
        const trees = response.data?.payload ?? []
        setMenuTrees(trees)
        setSelectedTreeId((current) => current || trees[0]?.id || '')
      })
      .catch(() => showErrorToast('Menü ağaçları yüklenemedi'))
  }, [])

  const tabOptions = [
    { key: 'form' as const, title: 'Rol Tanımlama', icon: 'bi bi-shield-lock' },
    { key: 'list' as const, title: 'Rol Listesi', icon: 'bi bi-list' },
  ]

  // Yetkileri modüle göre grupla
  const groupedPermissions = permissions.reduce<Record<string, IPermission[]>>((acc, perm) => {
    const module = perm.module || 'DİĞER'
    if (!acc[module]) acc[module] = []
    acc[module].push(perm)
    return acc
  }, {})

  const filteredGrouped = Object.entries(groupedPermissions).reduce<Record<string, IPermission[]>>(
    (acc, [module, perms]) => {
      const filtered = perms.filter(
        (p) =>
          p.name.toLowerCase().includes(permissionSearch.toLowerCase()) ||
          p.description.toLowerCase().includes(permissionSearch.toLowerCase())
      )
      if (filtered.length > 0) acc[module] = filtered
      return acc
    },
    {}
  )

  const fetchRoles = useCallback(async (page = 0) => {
    setLoading(true)
    try {
      const response = await loadRoles(page)
      if (response.data.hasError) throw new Error(response.data.message)
      setRoles(response.data.payload.content)
      setTotalPages(response.data.payload.totalPages)
    } catch (err) {
      showErrorToast('Roller yüklenemedi')
    } finally {
      setLoading(false)
    }
  }, [])

  const fetchPermissions = useCallback(async () => {
    if (!selectedTreeId) {
      setPermissions([])
      return
    }
    try {
      const response = await loadAllMenus(selectedTreeId)
      if (!response.data.hasError) {
        const payload = response.data.payload
        const menuItems = Array.isArray(payload) ? payload : payload?.content ?? []
        const rootMenus = menuItems.filter((m: any) => m.parentId === null && m.isActive)

        const PERMISSION_TYPES = [
          {suffix: 'view', label: 'Görüntüle', desc: 'görüntüleme'},
          {suffix: 'create', label: 'Ekle', desc: 'ekleme'},
          {suffix: 'update', label: 'Güncelle', desc: 'güncelleme'},
          {suffix: 'delete', label: 'Sil', desc: 'silme'},
        ]

        const dynamicPermissions: IPermission[] = rootMenus.flatMap((menu: any) => {
          const moduleKey = menu.title.toUpperCase().replace(/\s+/g, '_')
          return PERMISSION_TYPES.map((pt) => ({
            id: `${menu.id}_${pt.suffix}`,
            name: `${menu.title} ${pt.label}`,
            description: `${menu.title} ${pt.desc} yetkisi`,
            module: moduleKey,
          }))
        })

        setPermissions(dynamicPermissions)

        const dynamicModuleLabels: Record<string, string> = {}
        rootMenus.forEach((menu: any) => {
          dynamicModuleLabels[menu.title.toUpperCase().replace(/\s+/g, '_')] = menu.title
        })
        setMenuModuleLabels(dynamicModuleLabels)
      }
    } catch {
      setPermissions(MOCK_PERMISSIONS)
    }
  }, [selectedTreeId])

  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  useEffect(() => {
    if (tab === 'list') fetchRoles(0)
  }, [tab, fetchRoles])

  const handleSave = async (values: IRoleModel, resetForm: () => void) => {
    setLoading(true)
    try {
      const payload = { ...values, permissions: selectedPermissions }
      if (selectedId) {
        await updateRole({ ...payload, id: selectedId })
        showSuccessToast('Rol başarıyla güncellendi')
      } else {
        await createRole(payload)
        showSuccessToast('Rol başarıyla oluşturuldu')
      }
      resetForm()
      setSelectedId(null)
      setSelectedPermissions([])
      setTab('list')
      fetchRoles(0)
    } catch {
      showErrorToast('İşlem sırasında hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (role: IRoleModel) => {
    const roleId: string | null = role.id ? role.id : null
    setSelectedId(roleId)
    setSelectedPermissions(role.permissions || [])
    setTab('form')
  }

  const handleDeleteClick = (role: IRoleModel) => {
    const roleId: string | null = role.id ? role.id : null
    setDeleteId(roleId)
    setShowModal(true)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteId) return
    setLoading(true)
    try {
      await deleteRole(deleteId)
      showSuccessToast('Rol silindi')
      setShowModal(false)
      setDeleteId(null)
      fetchRoles(currentPage)
    } catch {
      showErrorToast('Silme işlemi başarısız')
    } finally {
      setLoading(false)
    }
  }

  const togglePermission = (permId: string) => {
    setSelectedPermissions((prev) =>
      prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]
    )
  }

  const toggleModule = (modulePerms: IPermission[]) => {
    const ids = modulePerms.map((p) => p.id)
    const allSelected = ids.every((id) => selectedPermissions.includes(id))
    if (allSelected) {
      setSelectedPermissions((prev) => prev.filter((p) => !ids.includes(p)))
    } else {
      setSelectedPermissions((prev) => Array.from(new Set([...prev, ...ids])))
    }
  }

  const selectedRole = roles.find((r) => r.id === selectedId) || initialRole

  return (
    <div className='card'>
      <ToastContainer />
      <ConfirmModal
        show={showModal}
        header='Rol Sil'
        text='Bu rolü silmek istediğinize emin misiniz?'
        handleClose={() => setShowModal(false)}
        applyOnClick={handleDeleteConfirm}
      />

      <div className='card-header border-0 pt-6'>
        <div className='card-title'>
          <h3 className='card-label fw-bold fs-3'>
            <i className='bi bi-shield-lock-fill me-2 text-primary' />
            Rol ve Yetki Tanımları
          </h3>
        </div>
      </div>

      <div className='card-body pt-0'>
        {/* Tab butonları */}
        <div className='d-flex flex-wrap gap-1 mb-5'>
          {tabOptions.map((t) => (
            <button
              key={t.key}
              type='button'
              className={`btn btn-sm ${tab === t.key ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setTab(t.key)}
            >
              {t.icon && <i className={`${t.icon} me-2`} />}
              {t.title}
            </button>
          ))}
        </div>

        {/* FORM TAB */}
        {tab === 'form' && (
          <Formik
            enableReinitialize
            initialValues={selectedRole}
            validationSchema={validationRole}
            onSubmit={(values, { resetForm }) => handleSave(values, resetForm)}
          >
            {(formik: FormikProps<IRoleModel>) => (
              <Form>
                <div className='row g-5 mt-2'>
                  {/* Sol: Rol bilgileri */}
                  <div className='col-lg-4'>
                    <div className='card border'>
                      <div className='card-header min-h-50px'>
                        <h4 className='card-title fs-6 fw-bold'>
                          <i className='bi bi-info-circle me-2 text-primary' />
                          Rol Bilgileri
                        </h4>
                      </div>
                      <div className='card-body p-5'>
                        <TextInput name='roleName' label='Rol Adı' placeholder='Örn: Kiralama Uzmanı' required />
                        <TextInput name='description' label='Açıklama' placeholder='Rol açıklaması' required />

                        <div className='mb-5'>
                          <label className='form-label fw-bold'>Durum</label>
                          <div className='form-check form-switch'>
                            <input
                              className='form-check-input'
                              type='checkbox'
                              id='activeSwitch'
                              checked={formik.values.active}
                              onChange={(e) => formik.setFieldValue('active', e.target.checked)}
                            />
                            <label className='form-check-label' htmlFor='activeSwitch'>
                              {formik.values.active ? (
                                <span className='badge badge-light-success'>Aktif</span>
                              ) : (
                                <span className='badge badge-light-danger'>Pasif</span>
                              )}
                            </label>
                          </div>
                        </div>

                        {/* Seçili yetki özeti */}
                        <div className='separator my-4' />
                        <div className='d-flex justify-content-between align-items-center mb-3'>
                          <span className='text-muted fs-7'>Seçili Yetki</span>
                          <span className='badge badge-primary'>{selectedPermissions.length}</span>
                        </div>
                        {selectedPermissions.length > 0 && (
                          <button
                            type='button'
                            className='btn btn-sm btn-light-danger w-100'
                            onClick={() => setSelectedPermissions([])}
                          >
                            <i className='bi bi-x-circle me-1' />
                            Tümünü Temizle
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Kaydet butonu */}
                    <div className='d-flex gap-3 mt-5'>
                      <button
                        type='submit'
                        className='btn btn-primary flex-fill'
                        disabled={loading || formik.isSubmitting}
                      >
                        {loading ? (
                          <span className='spinner-border spinner-border-sm me-2' />
                        ) : (
                          <i className='bi bi-check-lg me-2' />
                        )}
                        {selectedId ? 'Güncelle' : 'Kaydet'}
                      </button>
                      <button
                        type='button'
                        className='btn btn-light flex-fill'
                        onClick={() => {
                          formik.resetForm()
                          setSelectedId(null)
                          setSelectedPermissions([])
                        }}
                      >
                        <i className='bi bi-arrow-counterclockwise me-2' />
                        Temizle
                      </button>
                    </div>
                  </div>

                  {/* Sağ: Yetki seçimi */}
                  <div className='col-lg-8'>
                    <div className='card border'>
                      <div className='card-header min-h-50px'>
                        <h4 className='card-title fs-6 fw-bold'>
                          <i className='bi bi-key me-2 text-warning' />
                          Yetki Ataması
                        </h4>
                        <div className='card-toolbar gap-2'>
                          <select
                            className='form-select form-select-sm w-200px'
                            title='Menü ağacı'
                            value={selectedTreeId}
                            onChange={(e) => setSelectedTreeId(e.target.value)}
                          >
                            {menuTrees.length === 0 && <option value=''>Menü ağacı yok</option>}
                            {menuTrees.map((tree) => (
                              <option key={tree.id} value={tree.id}>
                                {tree.name}
                              </option>
                            ))}
                          </select>
                          <input
                            type='text'
                            className='form-control form-control-sm w-200px'
                            placeholder='Yetki ara...'
                            value={permissionSearch}
                            onChange={(e) => setPermissionSearch(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className='card-body p-5' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                        {menuTrees.length > 1 && (
                          <div className='text-muted fs-7 mb-4'>
                            <i className='bi bi-info-circle me-1' />
                            Ağaç değiştirildiğinde diğer ağaçlarda seçtiğiniz yetkiler korunur.
                          </div>
                        )}
                        {Object.entries(filteredGrouped).length === 0 ? (
                          <div className='text-center text-muted py-10'>
                            <i className='bi bi-search fs-2 mb-3 d-block' />
                            Yetki bulunamadı
                          </div>
                        ) : (
                          Object.entries(filteredGrouped).map(([module, perms]) => {
                            const allSelected = perms.every((p) =>
                              selectedPermissions.includes(p.id)
                            )
                            const someSelected = perms.some((p) =>
                              selectedPermissions.includes(p.id)
                            )
                            return (
                              <div key={module} className='mb-6'>
                                {/* Modül başlığı */}
                                <div
                                  className='d-flex align-items-center justify-content-between bg-light-primary rounded p-3 mb-3 cursor-pointer'
                                  onClick={() => toggleModule(perms)}
                                >
                                  <div className='d-flex align-items-center gap-2'>
                                    <div
                                      className={`form-check-input border border-primary ${
                                        allSelected
                                          ? 'bg-primary'
                                          : someSelected
                                          ? 'bg-primary opacity-50'
                                          : ''
                                      }`}
                                      style={{ width: '18px', height: '18px', borderRadius: '4px' }}
                                    />
                                    <span className='fw-bold text-primary fs-6'>
                                      {menuModuleLabels[module] || MODULE_LABELS[module] || module}
                                    </span>
                                  </div>
                                  <span className='badge badge-light-primary'>
                                    {perms.filter((p) => selectedPermissions.includes(p.id)).length}/
                                    {perms.length}
                                  </span>
                                </div>

                                {/* Yetkiler */}
                                <div className='row g-2 ps-3'>
                                  {perms.map((perm) => {
                                    const checked = selectedPermissions.includes(perm.id)
                                    return (
                                      <div key={perm.id} className='col-md-6'>
                                        <div
                                          className={`d-flex align-items-start gap-2 p-3 rounded border cursor-pointer transition ${
                                            checked
                                              ? 'border-primary bg-light-primary'
                                              : 'border-dashed border-gray-300'
                                          }`}
                                          onClick={() => togglePermission(perm.id)}
                                        >
                                          <input
                                            type='checkbox'
                                            className='form-check-input mt-1'
                                            checked={checked}
                                            onChange={() => togglePermission(perm.id)}
                                          />
                                          <div>
                                            <div className='fw-semibold fs-7 text-dark'>
                                              {perm.name}
                                            </div>
                                            {perm.description && (
                                              <div className='text-muted fs-8'>
                                                {perm.description}
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  })}
                                </div>
                              </div>
                            )
                          })
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        )}

        {/* LIST TAB */}
        {tab === 'list' && (
          <div className='mt-5'>
            <RoleTable
              roles={roles}
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onPageChange={(page) => {
                setCurrentPage(page)
                fetchRoles(page)
              }}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
            />
          </div>
        )}
      </div>
    </div>
  )
}

// Backend hazır değilse gösterilecek mock yetki listesi
const MOCK_PERMISSIONS: IPermission[] = [
  { id: 'car_view', name: 'Araç Görüntüle', description: 'Araç listesini görüntüleme', module: 'CAR' },
  { id: 'car_create', name: 'Araç Ekle', description: 'Yeni araç oluşturma', module: 'CAR' },
  { id: 'car_update', name: 'Araç Güncelle', description: 'Araç bilgilerini güncelleme', module: 'CAR' },
  { id: 'car_delete', name: 'Araç Sil', description: 'Araç silme', module: 'CAR' },
  { id: 'customer_view', name: 'Müşteri Görüntüle', description: 'Müşteri listesini görüntüleme', module: 'CUSTOMER' },
  { id: 'customer_create', name: 'Müşteri Ekle', description: 'Yeni müşteri oluşturma', module: 'CUSTOMER' },
  { id: 'customer_update', name: 'Müşteri Güncelle', description: 'Müşteri bilgilerini güncelleme', module: 'CUSTOMER' },
  { id: 'customer_delete', name: 'Müşteri Sil', description: 'Müşteri silme', module: 'CUSTOMER' },
  { id: 'reservation_view', name: 'Rezervasyon Görüntüle', description: 'Rezervasyon listesi', module: 'RESERVATION' },
  { id: 'reservation_create', name: 'Rezervasyon Oluştur', description: 'Yeni rezervasyon', module: 'RESERVATION' },
  { id: 'reservation_cancel', name: 'Rezervasyon İptal', description: 'Rezervasyon iptal etme', module: 'RESERVATION' },
  { id: 'rent_view', name: 'Kiralama Görüntüle', description: 'Kiralama listesi', module: 'RENT' },
  { id: 'rent_create', name: 'Kiralama Oluştur', description: 'Yeni kiralama', module: 'RENT' },
  { id: 'rent_close', name: 'Kiralama Kapat', description: 'Kiralamayı kapatma', module: 'RENT' },
  { id: 'definition_view', name: 'Tanım Görüntüle', description: 'Tanım ekranlarını görüntüleme', module: 'DEFINITION' },
  { id: 'definition_manage', name: 'Tanım Yönet', description: 'Tanım ekleme/güncelleme/silme', module: 'DEFINITION' },
  { id: 'finance_view', name: 'Finans Görüntüle', description: 'Finans ekranlarını görüntüleme', module: 'FINANCE' },
  { id: 'finance_manage', name: 'Finans Yönet', description: 'Finans işlemleri yapma', module: 'FINANCE' },
  { id: 'report_view', name: 'Rapor Görüntüle', description: 'Raporları görüntüleme', module: 'REPORT' },
  { id: 'report_export', name: 'Rapor İndir', description: 'Rapor dışa aktarma', module: 'REPORT' },
  { id: 'admin_users', name: 'Kullanıcı Yönetimi', description: 'Kullanıcı ekleme/silme', module: 'ADMIN' },
  { id: 'admin_roles', name: 'Rol Yönetimi', description: 'Rol ve yetki tanımlama', module: 'ADMIN' },
  { id: 'admin_settings', name: 'Sistem Ayarları', description: 'Sistem konfigürasyonu', module: 'ADMIN' },
]

export { RolePage }
